import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '../types';
import { useAuthStore } from '@/features/auth';
import { ConversationPicker } from './ConversationPicker';
import { chatHaptic } from '../utils/chatHaptic';

interface ForwardMessageProps {
  message: ChatMessage;
  isOpen: boolean;
  onClose: () => void;
  onForward: (conversationIds: string[], additionalMessage?: string) => void;
}

export function ForwardMessage({ message, isOpen, onClose, onForward }: ForwardMessageProps) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const [selectedConversationIds, setSelectedConversationIds] = useState<string[]>([]);
  const [additionalMessage, setAdditionalMessage] = useState('');

  const handleToggleConversation = (conversationId: string) => {
    setSelectedConversationIds((prev) =>
      prev.includes(conversationId)
        ? prev.filter((id) => id !== conversationId)
        : prev.length < 5
        ? [...prev, conversationId]
        : prev
    );
  };

  const handleForward = () => {
    if (selectedConversationIds.length > 0) {
      chatHaptic.action('forward');
      onForward(selectedConversationIds, additionalMessage.trim() || undefined);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedConversationIds([]);
    setAdditionalMessage('');
    onClose();
  };

  const getMessagePreview = () => {
    switch (message.type) {
      case 'image':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" strokeWidth="2" />
            </svg>
            <span>{t('messaging.forward.imageMessage')}</span>
          </div>
        );
      case 'video':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="2" width="20" height="20" rx="2" ry="2" strokeWidth="2" />
              <polygon points="10,8 16,12 10,16" fill="currentColor" />
            </svg>
            <span>{t('messaging.forward.videoMessage')}</span>
          </div>
        );
      case 'file':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" strokeWidth="2" />
              <polyline points="13 2 13 9 20 9" strokeWidth="2" />
            </svg>
            <span>{(message.mediaMetadata as Record<string, string>)?.fileName || t('messaging.forward.fileMessage')}</span>
          </div>
        );
      case 'audio':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M9 18V5l12-2v13" strokeWidth="2" />
              <circle cx="6" cy="18" r="3" strokeWidth="2" />
              <circle cx="18" cy="16" r="3" strokeWidth="2" />
            </svg>
            <span>{t('messaging.forward.audioMessage')}</span>
          </div>
        );
      default:
        return (
          <p className="text-neutral-800/90 line-clamp-2">
            {message.content || t('messaging.forward.emptyMessage')}
          </p>
        );
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-[9998]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
          >
            <div className="bg-white/40 border border-white/20 shadow-sm w-full max-w-lg max-h-[85dvh] flex flex-col rounded-3xl overflow-hidden glass-surface">
              {/* Header */}
              <div className="border-b border-white/20 px-5 py-3 flex items-center justify-between">
                <h2 className="font-semibold text-xs sm:text-sm">
                  {t('messaging.forward.title')}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                  aria-label={t('common.close')}
                >
                  <svg className="w-6 h-6 text-neutral-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Message Preview */}
              <div className="border-b border-white/20 p-4 bg-white/60">
                <div className="text-xs font-medium text-neutral-500 mb-1">
                  {message.senderId === currentUserId
                    ? t('messaging.forward.you')
                    : message.sender?.name || t('messaging.forward.unknown')}
                </div>
                {getMessagePreview()}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Conversation Picker */}
                <div>
                  <label className="block text-sm font-medium text-neutral-800 mb-2">
                    {t('messaging.forward.selectRecipients')} ({selectedConversationIds.length}/5)
                  </label>
                  <ConversationPicker
                    selectedIds={selectedConversationIds}
                    onToggle={handleToggleConversation}
                    excludeIds={[message.conversationId]}
                    maxSelections={5}
                  />
                </div>

                {/* Additional Message */}
                <div>
                  <label htmlFor="additional-message" className="block text-sm font-medium text-neutral-800 mb-2">
                    {t('messaging.forward.addMessage')} ({t('messaging.forward.optional')})
                  </label>
                  <textarea
                    id="additional-message"
                    value={additionalMessage}
                    onChange={(e) => setAdditionalMessage(e.target.value)}
                    placeholder={t('messaging.forward.addMessagePlaceholder')}
                    className="w-full px-3 py-2 rounded-xl border border-white/20 bg-white/80 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-slate-400 font-medium resize-none"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-neutral-500 mt-1 text-right">
                    {additionalMessage.length}/500
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-white/20 p-4 flex gap-3 justify-end bg-white/60">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-xl border border-white/20 bg-white/80 text-neutral-800 font-medium hover:bg-neutral-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleForward}
                  disabled={selectedConversationIds.length === 0}
                  className="px-4 py-2 rounded-xl border border-slate-400 bg-slate-700 text-white font-medium hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700"
                >
                  {t('messaging.forward.forwardButton')}
                  {selectedConversationIds.length > 0 && ` (${selectedConversationIds.length})`}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
